import HIIFreight from '@shared/hireInvoice/HIIFreight';
import HireInvoiceItem from '@shared/hireInvoice/HireInvoiceItem';
import HIIDeadfreight from '@shared/hireInvoice/HIIDeadfreight';
import getFDDNumbers from '@shared/utils/getFDDNumbers';
import HIIOverage from '@shared/hireInvoice/HIIOverage';
import getLaytimeStats from '@shared/utils/laytimeStats/getLaytimeStats';
import HIIDemurrage from '@shared/hireInvoice/HIIDemurrage';
import HIIDespatch from '@shared/hireInvoice/HIIDespatch';
import HIIVoyageExpense from '@shared/hireInvoice/HIIVoyageExpense';
import HIIRevenueItem from '@shared/hireInvoice/HIIRevenueItem';
import HIIFreightAddressCommission from '@shared/hireInvoice/HIIFreightAddressCommission';
import HIIFreightBrokerCommission from '@shared/hireInvoice/HIIFreightBrokerCommission';
import { BankAccountProps } from '@api/models/bank-account';
import { VoyageInvoice } from '@api/utils/sequelize/getAllVoyageInvoices';
import { VoyageExpenseReceivableProps } from '@api/models/voyage-expense-receivable';
import { RevenueItemProps } from '@api/models/revenue-item';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import type { CargoWithPortsAndCalculationsJSON } from '@api/features/laytime/getCargosAndLaytimes';
import { Values } from './objectEnums';
import { calculateTotal } from './math';
import {
	AccountTypes,
	CargoContractSides,
	FixtureTypes,
	HireInvoiceItemStates,
} from './constants';
import {
	getFreightHireInvoiceItem,
	getDeadfreightOrOverageHireInvoiceItem,
	getDndInvoiceItems,
	getExpenseHireInvoiceItem,
	getRevenueItemHireInvoiceItem,
} from './hireInvoice';
import filterHireInvoiceItemsByState from './filterHireInvoiceItemsByState';
import {
	getPendingAmendmentItems,
	linkChildrenToParents,
} from './getPendingAmendmentItems';

let _id = -1;

const getId = () => {
	const newId = _id;

	_id -= 1;

	return newId;
};

export type WithHIIState<T> = T & { state: Values<typeof HireInvoiceItemStates> };

export type SpotParams = {
	hireInvoices: VoyageInvoice[];
	brokers: Array<{
		id: number;
		name: string;
		commission: number | null;
		paidBy: Values<typeof AccountTypes>;
	}>;
	cargos: CargoWithPortsAndCalculationsJSON[];
	selectedBankAccount: BankAccountProps;
	voyageExpenseReceivables: WithHIIState<VoyageExpenseReceivableProps>[];
	revenueItems: WithHIIState<RevenueItemProps>[];
	chartererId: number;
	type: typeof FixtureTypes.SPOT;
	amendments: GetVoyageDetailsResponse['amendments'];
	contractSide?: CargoContractSides;
};

const isOriginalInvoicedItem = (item: HireInvoiceItem) => (
	!item.isReversal && !item.isAmendment
);

const getAvailableFreightInvoiceItems = (params: SpotParams) => {
	const {
		hireInvoices,
		selectedBankAccount,
		cargos,
		chartererId,
		voyageExpenseReceivables,
		revenueItems,
		amendments,
		contractSide = CargoContractSides.VC_OUT,
	} = params;

	const cargoResults: (
		HIIFreight | HIIDeadfreight | HIIOverage | HIIDemurrage | HIIDespatch | HIIVoyageExpense | HIIRevenueItem | HireInvoiceItem
	)[] = [];

	if (!Array.isArray(hireInvoices)) {
		return cargoResults;
	}

	const isVcIn = contractSide === CargoContractSides.VC_IN;
	const invoicesById = new Map(hireInvoices.map((invoice) => [invoice.id, invoice]));

	const { items: pendingAmendmentItems } = getPendingAmendmentItems(amendments ?? [], (amendment) => {
		const originalInvoice = invoicesById.get(amendment.originalItem.hireInvoiceId as number);

		return (
			originalInvoice != null &&
			originalInvoice.chartererId === chartererId &&
			Boolean(originalInvoice.isCargoRelet) === isVcIn
		);
	});

	const invoicedItems = hireInvoices.flatMap((invoice) => invoice.items);

	const invoicedSourceIds = [
		...invoicedItems,
		...pendingAmendmentItems,
	].reduce((ids, item) => {
		const { voyageExpenseReceivableId, revenueItemId } = item as unknown as {
			voyageExpenseReceivableId?: number | null;
			revenueItemId?: number | null;
		};

		if (voyageExpenseReceivableId != null) {
			ids.expenses.add(voyageExpenseReceivableId);
		}

		if (revenueItemId != null) {
			ids.revenueItems.add(revenueItemId);
		}

		return ids;
	}, { expenses: new Set<number>(), revenueItems: new Set<number>() });

	const invoicedFreights = invoicedItems
		.filter((i): i is HIIFreight => (
			i.itemTypeLabel === 'Freight' && isOriginalInvoicedItem(i)
		));

	const invoicedDeadfreights = invoicedItems
		.filter((i): i is HIIDeadfreight => (
			i.itemTypeLabel === 'Deadfreight' && isOriginalInvoicedItem(i)
		));

	const invoicedOverages = invoicedItems
		.filter((i): i is HIIOverage => (
			i.itemTypeLabel === 'Overage' && isOriginalInvoicedItem(i)
		));

	filterHireInvoiceItemsByState(voyageExpenseReceivables, HireInvoiceItemStates.PENDING)
		.filter((c) => (
			c.chartererId === chartererId &&
			!c.estimated &&
			!invoicedSourceIds.expenses.has(c.id)
		))
		.forEach((expense) => cargoResults.push(getExpenseHireInvoiceItem(
			getId,
			expense,
			selectedBankAccount.currency,
		)));

	filterHireInvoiceItemsByState(revenueItems, HireInvoiceItemStates.PENDING)
		.filter((c) => (
			c.chartererId === chartererId &&
			!c.estimated &&
			!invoicedSourceIds.revenueItems.has(c.id)
		))
		.forEach((revenueItem) => {
			const cargo = cargos.filter((c) => c.id === revenueItem.cargoId)[0];
			cargoResults.push(getRevenueItemHireInvoiceItem(
				getId,
				revenueItem,
				cargo,
				selectedBankAccount.currency,
			));
		});

	cargos
		.filter((c) => (
			c.charterer === chartererId &&
			(c.contractSide ?? CargoContractSides.VC_OUT) === contractSide
		))
		.forEach((cargo) => {
			const fddNumbers = getFDDNumbers(cargo);
			const laytimeStats = getLaytimeStats(cargo);
			let freight;
			let deadfreight;
			let overage;

			const invoicedFreightPercentage = calculateTotal(
				invoicedFreights.filter((i) => i.cargoId === cargo.id),
				(i) => i.percentage,
			);

			const relevantFreight = invoicedFreights.filter((i) => i.cargoId === cargo.id);
		
			// Freight
			const relevantFreightAddressCommission = 
				relevantFreight
					.map((i) => i.children.filter((v: any) => v.__itemType === 'freightAddressCommission'))
					.flat() as HIIFreightAddressCommission[];

			const invoicedFreightAddressCommission = calculateTotal(
				relevantFreightAddressCommission, (i) => i.invoiceablePercentage,
			);

			const relevantFreightBrokerCommission = 
				relevantFreight
					.map((i) => i.children.filter((v: any) => v.__itemType === 'freightBrokerCommission'))
					.flat() as unknown as HIIFreightBrokerCommission[];

			const invoicedFreightBrokerCommission = calculateTotal(
				relevantFreightBrokerCommission, (i) => i.invoiceablePercentage,
			);

			// Deadfreight
			const relevantDeadFreight = invoicedDeadfreights.filter((i) => i.cargoId === cargo.id);
			const relevantDeadfreightBrokerCommission = 
				relevantDeadFreight
					.map((i) => i.children.filter((v: any) => v.__itemType === 'freightBrokerCommission'))
					.flat() as unknown as HIIFreightBrokerCommission[];

			const invoicedDeadfreightBrokerCommission = calculateTotal(
				relevantDeadfreightBrokerCommission, (i) => i.invoiceablePercentage,
			);

			const relevantDeadfreightAddressCommission = 
				relevantDeadFreight
					.map((i) => i.children.filter((v: any) => v.__itemType === 'freightAddressCommission',))
					.flat() as HIIFreightAddressCommission[];

			const invoicedDeadfreightAddressCommission = calculateTotal(
				relevantDeadfreightAddressCommission, (i) => i.invoiceablePercentage,
			);

			// Overage
			const relevantOverage = invoicedOverages.filter((i) => i.cargoId === cargo.id);
			const relevantOverageBrokerCommission = 
				relevantOverage
					.map((i) => i.children.filter((v: any) => v.__itemType === 'freightBrokerCommission'))
					.flat() as unknown as HIIFreightAddressCommission[];

			const invoicedOverageBrokerCommission = calculateTotal(
				relevantOverageBrokerCommission, (i) => i.invoiceablePercentage,
			);

			const relevantOverageAddressCommission = 
				relevantOverage
					.map((i) => i.children.filter((v: any) => v.__itemType === 'freightAddressCommission'))
					.flat() as HIIFreightAddressCommission[];

			const invoicedOverageAddressCommission = calculateTotal(
				relevantOverageAddressCommission, (i) => i.invoiceablePercentage,
			);

			const invoicedDeadfreightPercentage = calculateTotal(
				invoicedDeadfreights.filter((i) => i.cargoId === cargo.id),
				(i) => i.percentage,
			);

			const invoicedOveragePercentage = calculateTotal(
				invoicedOverages.filter((i) => i.cargoId === cargo.id),
				(i) => i.percentage,
			);

			if ((100 - invoicedFreightPercentage) > 0) {
				freight = getFreightHireInvoiceItem(getId, cargo, selectedBankAccount.currency, {
					percentage: 100 - invoicedFreightPercentage,
					blQuantity: fddNumbers.totalFreightAmount,
					totalBlQuantity: fddNumbers.totalActuallyLoaded,
					addressCommissionInvoicablePercentage: 100 - invoicedFreightAddressCommission,
					brokerCommissionInvoicablePercentage: 100 - invoicedFreightBrokerCommission,
					maxAddressCommissionPercentage: 100 - invoicedFreightAddressCommission,
					maxBrokerCommissionPercentage: 100 - invoicedFreightBrokerCommission,
				}) as HIIFreight;
			}

			if ((100 - invoicedDeadfreightPercentage) > 0) {
				deadfreight = getDeadfreightOrOverageHireInvoiceItem(getId, cargo, selectedBankAccount.currency, {
					percentage: 100 - invoicedDeadfreightPercentage,
					quantity: fddNumbers.totalDeadfreightAmount,
					addressCommissionInvoicablePercentage: 100 - invoicedDeadfreightAddressCommission,
					brokerCommissionInvoicablePercentage: 100 - invoicedDeadfreightBrokerCommission,
					maxAddressCommissionPercentage: 100 - invoicedDeadfreightAddressCommission,
					maxBrokerCommissionPercentage: 100 - invoicedDeadfreightBrokerCommission,
					type: 'deadfreight',
				}) as HIIDeadfreight;
			}

			if ((100 - invoicedOveragePercentage) > 0) {
				overage = getDeadfreightOrOverageHireInvoiceItem(getId, cargo, selectedBankAccount.currency, {
					percentage: 100 - invoicedOveragePercentage,
					quantity: fddNumbers.totalOverageAmount,
					addressCommissionInvoicablePercentage: 100 - invoicedOverageAddressCommission,
					brokerCommissionInvoicablePercentage: 100 - invoicedOverageBrokerCommission,
					maxAddressCommissionPercentage: 100 - invoicedOverageAddressCommission,
					maxBrokerCommissionPercentage: 100 - invoicedOverageBrokerCommission,
					type: 'overage',
				}) as HIIOverage;
			}

			const dnd = getDndInvoiceItems(
				getId,
				cargo,
				selectedBankAccount.currency,
				laytimeStats,
				hireInvoices,
				chartererId,
			);

			cargoResults.push(
				...(freight != null ? [freight] : []),
				...(deadfreight != null ? [deadfreight] : []),
				...(overage != null ? [overage] : []),
				...(dnd ?? []),
			);
		});

	cargoResults.push(...pendingAmendmentItems);

	linkChildrenToParents(cargoResults);

	return cargoResults;
};

export default getAvailableFreightInvoiceItems;
